import type { DataRow } from "../types";

export type DateRangePreset = "all" | "today" | "7d" | "30d" | "month" | "custom";

export type GlobalDateRange = {
  preset: DateRangePreset;
  dateFrom: string;
  dateTo: string;
};

export const ALL_DATES: GlobalDateRange = { preset: "all", dateFrom: "", dateTo: "" };

export function reportedDateTime(row: DataRow): string | null {
  const received = String(row.receivedAt ?? "").trim();
  if (received) return received;
  const opened = String(row.openedAt ?? "").trim();
  if (!opened) return null;
  const time = String(row.receivedTime ?? "").trim();
  return time && /^\d{1,2}:\d{2}/.test(time) && !opened.includes("T")
    ? `${opened.slice(0, 10)}T${time}`
    : opened;
}

export function reportedDate(row: DataRow): string {
  return reportedDateTime(row)?.slice(0, 10) ?? "";
}

export function filterRowsByReportedDate(rows: DataRow[], range: GlobalDateRange): DataRow[] {
  if (!range.dateFrom && !range.dateTo) return rows;
  return rows.filter((row) => {
    const date = reportedDate(row);
    if (!date) return false;
    if (range.dateFrom && date < range.dateFrom) return false;
    if (range.dateTo && date > range.dateTo) return false;
    return true;
  });
}

function isoLocalDate(date: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Bangkok", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(date);
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
}

function shiftDays(date: string, amount: number): string {
  const value = new Date(`${date}T12:00:00+07:00`);
  value.setUTCDate(value.getUTCDate() + amount);
  return isoLocalDate(value);
}

export function presetDateRange(preset: Exclude<DateRangePreset, "custom">, now = new Date()): GlobalDateRange {
  if (preset === "all") return ALL_DATES;
  const today = isoLocalDate(now);
  if (preset === "today") return { preset, dateFrom: today, dateTo: today };
  if (preset === "month") return { preset, dateFrom: `${today.slice(0, 8)}01`, dateTo: today };
  const days = preset === "7d" ? 7 : 30;
  return { preset, dateFrom: shiftDays(today, -(days - 1)), dateTo: today };
}

